/**
 * `ConnectionDiagnosticsDialog` — Slice 3 (+ Slice 11 NO_POOL Reconnect).
 *
 * A focused modal that renders a `DiagnosticsReport` in human-friendly form,
 * with copy-to-clipboard and "Send diagnostic report" actions. See
 * `Ralph/ConnectionDiagnostics/PRD.md` → Slices 3 + 11 for the full spec.
 *
 * The dialog is mounted once globally from `App.tsx` — every UI consumer
 * routes through `showConnectionDiagnostics(...)` (which `registerHandler`
 * has wired to this dialog at app boot).
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { toast } from '../../../components/ui/toaster';
import { useAnalytics } from '../../../core/analytics/useAnalytics';
import { writeClipboardText } from '../../../lib/clipboard';
import { useAppStore } from '../../../store/appStore';
import { useConnectionStore } from '../../connections/store/connectionStore';
import type { DiagnosticsReport } from '../../../types/connectionDiagnostics';
import { useDiagnosticsDialogStore } from './diagnosticsDialogStore';
import {
  DiagnosticsDialogBody,
  DiagnosticsLoadingSkeleton,
  type DiagnosticsReconnectState,
  type DiagnosticsSendState,
} from './DiagnosticsDialogBody';
import { DiagnosticsSendPreviewDialog } from './DiagnosticsSendPreviewDialog';
import { useDiagnosticsFetch } from './useDiagnosticsFetch';
import {
  formatRedactedPayloadForClipboard,
  redactDiagnosticPayload,
} from '../utils/redactDiagnosticPayload';
import { reconnectAndInvalidate } from '../utils/reconnectAndRerunDiagnostics';

export function ConnectionDiagnosticsDialog() {
  const { open, args, closeDialog } = useDiagnosticsDialogStore();
  const crashReportMode = useAppStore((s) => s.crashReportMode);
  const connections = useConnectionStore((s) => s.connections);
  const [sendState, setSendState] = useState<DiagnosticsSendState>('idle');
  const [reconnectState, setReconnectState] =
    useState<DiagnosticsReconnectState>('idle');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [refetchKey, setRefetchKey] = useState(0);
  const { track } = useAnalytics();
  const { fetchState } = useDiagnosticsFetch({
    open,
    dialogArgs: args,
    refetchKey,
  });

  // Reset transient UI state when the dialog opens for a new (connection,
  // trigger, scope) tuple — mirrors the original Slice 3 behaviour.
  useEffect(() => {
    if (!open || !args) return;
    setSendState('idle');
    setReconnectState('idle');
    setPreviewOpen(false);
  }, [open, args]);

  const handleCopy = async (report: DiagnosticsReport) => {
    try {
      const payload = await redactDiagnosticPayload(report);
      const text = formatRedactedPayloadForClipboard(payload);
      const ok = await writeClipboardText(text);
      if (ok) {
        track('feature:connection_diagnostics_copy');
        toast.success({ title: 'Diagnostic details copied to clipboard.' });
      } else {
        toast.error({ title: 'Failed to copy diagnostic details.' });
      }
    } catch {
      toast.error({ title: 'Failed to copy diagnostic details.' });
    }
  };

  const handleOpenPreview = () => {
    setSendState('idle');
    setPreviewOpen(true);
  };

  const handleConfirmSend = async (report: DiagnosticsReport) => {
    setSendState('sending');
    try {
      const sender = window.electronAPI?.sendDiagnosticReport;
      const result = sender ? await sender({ report }) : null;
      if (result) {
        setSendState('sent');
        track('feature:connection_diagnostics_send');
        setPreviewOpen(false);
      } else {
        setSendState('failed');
      }
    } catch {
      setSendState('failed');
    }
  };

  const handleCancelPreview = () => {
    if (sendState === 'sending') return;
    setPreviewOpen(false);
    if (sendState === 'failed') setSendState('idle');
  };

  const handleReconnect = async () => {
    if (!args) return;
    const connection = connections.find((c) => c.id === args.connectionId);
    if (!connection) {
      setReconnectState('failed');
      return;
    }
    setReconnectState('reconnecting');
    const outcome = await reconnectAndInvalidate(connection);
    if (outcome.success) {
      track('feature:connection_diagnostics_reconnect');
      // Dismiss dialog and trigger a fresh listing run by the host that
      // opened us. Any subsequent open re-fetches because the cache for
      // this connectionId was invalidated by `reconnectAndInvalidate`.
      setReconnectState('idle');
      closeDialog();
    } else {
      setReconnectState('failed');
      // Force the diagnostics fetch to re-run so the dialog body reflects
      // the new error reason (which may now differ from `'no_pool'`).
      setRefetchKey((k) => k + 1);
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) closeDialog();
  };

  const sendIpcAvailable =
    typeof window !== 'undefined' && !!window.electronAPI?.sendDiagnosticReport;
  const showSendButton = crashReportMode !== 'never' && sendIpcAvailable;

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Connection diagnostics</DialogTitle>
            <DialogDescription>
              What the server returned about this connection.
            </DialogDescription>
          </DialogHeader>
          {fetchState.kind === 'loading' && <DiagnosticsLoadingSkeleton />}
          {fetchState.kind === 'error' && (
            <div className="text-sm text-destructive" data-testid="diag-fetch-error">
              Couldn't run diagnostics: {fetchState.message}
            </div>
          )}
          {fetchState.kind === 'ready' && (
            <DiagnosticsDialogBody
              report={fetchState.report}
              sendState={sendState}
              showSendButton={showSendButton}
              crashReportMode={crashReportMode}
              onCopy={() => void handleCopy(fetchState.report)}
              onSend={handleOpenPreview}
              onReconnect={() => void handleReconnect()}
              reconnectState={reconnectState}
            />
          )}
        </DialogContent>
      </Dialog>
      <DiagnosticsSendPreviewDialog
        open={previewOpen && fetchState.kind === 'ready'}
        report={fetchState.kind === 'ready' ? fetchState.report : null}
        sendState={sendState}
        onCancel={handleCancelPreview}
        onConfirm={() => {
          if (fetchState.kind === 'ready') {
            void handleConfirmSend(fetchState.report);
          }
        }}
      />
    </>
  );
}
